import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import { RiArrowDropDownLine } from 'react-icons/ri';
import styled from 'styled-components';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { ReactComponent as DeleteCircle } from '../../../res/assets/deleteCircle.svg';
import colors from '../../../res/styles/Colors';
import themebutton from '../../../res/styles/theme_button.module.scss';

ResourceTagSelect.propTypes = {
    allTags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    selectedTags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    onChange: PropTypes.func,
    onDeleteTag: PropTypes.func
};

ResourceTagSelect.defaultProps = {
    allTags: [],
    selectedTags: [],
    onChange: () => {},
    onDeleteTag: () => {}
};

export default function ResourceTagSelect({ allTags, selectedTags, onChange, onDeleteTag }) {
    const [inputValue, setInputValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [tagToRemove, setTagToRemove] = useState(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedNames = selectedTags.map((t) => t.name.toLowerCase());

    const filteredOptions = allTags.filter(
        (t) =>
            t.name.toLowerCase().includes(inputValue.toLowerCase().trim()) &&
            !selectedNames.includes(t.name.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setIsDropdownOpen(true);
    };

    const handleSelectOption = (tag) => {
        onChange([...selectedTags, tag]);
        setInputValue('');
        setIsDropdownOpen(false);
        inputRef.current?.focus();
    };

    const handleCreateNew = () => {
        const name = inputValue.trim();
        if (!name || selectedNames.includes(name.toLowerCase())) return;
        onChange([...selectedTags, { id: null, name }]);
        setInputValue('');
        setIsDropdownOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const exact = filteredOptions.find(
                (t) => t.name.toLowerCase() === inputValue.trim().toLowerCase()
            );
            if (exact) handleSelectOption(exact);
            else if (inputValue.trim()) handleCreateNew();
        } else if (e.key === 'Escape') {
            setIsDropdownOpen(false);
        }
    };

    const handleRemoveRequest = (tag) => {
        onChange(selectedTags.filter((t) => t.name !== tag.name));
    };

    const handleDeleteConfirm = async (_, confirmed) => {
        if (confirmed) {
            const tag = tagToRemove;
            setTagToRemove(null);
            await onDeleteTag(tag);
        } else {
            setTagToRemove(null);
        }
    };

    const showCreate =
        inputValue.trim() &&
        !allTags.some((t) => t.name.toLowerCase() === inputValue.trim().toLowerCase()) &&
        !selectedNames.includes(inputValue.trim().toLowerCase());

    return (
        <Container ref={containerRef}>
            <InputContainer>
                <StyledInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Select or write"
                />
                <DropdownArrow onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    <RiArrowDropDownLine />
                </DropdownArrow>
            </InputContainer>

            {isDropdownOpen && (filteredOptions.length > 0 || showCreate) && (
                <DropdownList>
                    {showCreate && (
                        <CreateOption onClick={handleCreateNew}>
                            + Create &quot;{inputValue.trim()}&quot;
                        </CreateOption>
                    )}
                    {filteredOptions.map((tag) => (
                        <DropdownItem key={tag.id}>
                            <span onClick={() => handleSelectOption(tag)}>{tag.name}</span>
                            <DeleteOptionButton
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setTagToRemove(tag); }}
                            >
                                <DeleteCircle width={18} height={18} />
                            </DeleteOptionButton>
                        </DropdownItem>
                    ))}
                </DropdownList>
            )}

            {selectedTags.length > 0 && (
                <TagsContainer>
                    {selectedTags.map((tag, i) => (
                        <Tag key={tag.id ?? `new-${i}`}>
                            <TagText>{tag.name}</TagText>
                            <RemoveButton
                                type="button"
                                onClick={() => handleRemoveRequest(tag)}
                            >
                                <IoMdClose />
                            </RemoveButton>
                        </Tag>
                    ))}
                </TagsContainer>
            )}

            <ConfirmDialog
                isOpen={tagToRemove !== null}
                text={`Remove tag?\nThis tag will be permanently deleted and removed from all resources.`}
                elementId={tagToRemove?.name ?? ''}
                cancelClassName={themebutton.gray}
                confirmClassName={themebutton.red}
                confirmText="REMOVE"
                onConfirm={handleDeleteConfirm}
            />
        </Container>
    );
}

const Container = styled.div`
    position: relative;
    width: 100%;
`;

const InputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const StyledInput = styled.input`
    height: 36px;
    width: 100%;
    border-radius: 2px;
    border: 1px solid #d9d9d9;
    padding-left: 10px;
    padding-right: 28px;
    font-size: 14px;

    &::placeholder {
        color: #bfbfbf;
    }

    &:focus {
        outline: none;
        border-color: ${colors.green};
    }

    @media (max-width: 480px) {
        font-size: 16px;
    }
`;

const DropdownArrow = styled.span`
    position: absolute;
    right: -2px;
    color: #000;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;

    svg {
        width: 26px;
        height: 26px;
    }
`;

const DropdownList = styled.div`
    position: absolute;
    top: 38px;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background: white;
    border-radius: 5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1050;
`;

const DropdownItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 10px;
    font-size: 14px;
    cursor: pointer;

    span {
        flex: 1;
    }

    &:hover {
        background-color: #f0f0f0;
    }

    &:hover > button {
        opacity: 1;
    }
`;

const DeleteOptionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.15s;
`;

const CreateOption = styled(DropdownItem)`
    color: ${colors.green};
    font-weight: 500;
    border-bottom: 1px solid #eee;

    &:hover {
        background-color: #e8f5e9;
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
`;

const Tag = styled.div`
    display: inline-flex;
    align-items: center;
    background-color: ${colors.green};
    color: white;
    padding: 0px 10px;
    border-radius: 8px;
    font-size: 13px;
`;

const TagText = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    margin-left: 6px;
    display: flex;
    align-items: center;

    svg {
        width: 14px;
        height: 14px;
    }

    &:hover {
        opacity: 0.8;
    }
`;

